// ABOUTME: The parts of the job details PJL adds: the places with their commute on a tap, the location check, the
// ABOUTME: weekend and holiday work, and the steps of the opening's process. Each renders nothing without its data.
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { useCommute } from '../hooks';
import {
  JobDetails,
  ListingLocation,
  WeekendWork,
  WorkFrequency
} from '../types';
import { formatKm, weekendDaysLabel } from '../utils';
import { JobPageNotice as Notice } from './JobPageParts';
import { ProcessStepper } from './ProcessStepper';

/** "Chausseestr. 112, 10115 Berlin" from whatever parts the address of a place of my job carries; nothing without one. */
const addressOf = (place: ListingLocation): string | undefined => {
  const { street, postalCode, city } = place.address ?? {};
  const town = [postalCode, city].filter(Boolean).join(' ');
  const line = [street, town].filter(Boolean).join(', ');
  return line || undefined;
};

/**
 * The places of a listing with several, the note that when and where is agreed, and the travel time on a tap where it
 * can be asked (only on mock data until pro-core serves it).
 */
export const Locations = ({ listing }: { listing: JobDetails }) => {
  const places = listing.workLocations ?? [];
  const commute = useCommute(listing.refId ?? '');
  // The commute is asked of a listing; the job of an application or of my job from its opening or assignment has none.
  const commuteOffered =
    commute.available &&
    !!listing.refId &&
    (listing.source === undefined || listing.source === 'LISTING');
  if (!places.length) return null;

  return (
    <Box data-testid="job-detail-locations">
      {places.length > 1 && (
        <Text
          variant="body2"
          color="text.secondary"
          component="p"
          sx={{ mb: 1 }}
        >
          {getTrans('jobs.detail.locationsAgreed')}
        </Text>
      )}
      {places.map(place => (
        <Box
          key={place.refId}
          data-testid="job-detail-location-row"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Text variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              {place.name}
            </Text>
            {addressOf(place) ? (
              <Text
                variant="caption"
                color="text.secondary"
                component="p"
                data-testid="job-detail-location-address"
              >
                {addressOf(place)}
              </Text>
            ) : (
              place.city && (
                <Text variant="caption" color="text.secondary" component="p">
                  {place.city}
                </Text>
              )
            )}
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            {place.distanceKm !== undefined && (
              <Text variant="body2" color="text.primary">
                {getTransWithParams('jobs.detail.km', {
                  km: formatKm(place.distanceKm)
                })}
              </Text>
            )}
            {commute.minutes?.[place.refId] !== undefined && (
              <Text
                variant="caption"
                color="text.secondary"
                component="p"
                data-testid="job-detail-commute-minutes"
              >
                {getTransWithParams('jobs.detail.commute.minutes', {
                  minutes: commute.minutes[place.refId]
                })}
              </Text>
            )}
          </Box>
        </Box>
      ))}
      {commuteOffered && !commute.asked && (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={commute.ask}
          data-testid="job-detail-commute-ask"
          sx={{ fontWeight: 600, mt: 1 }}
        >
          {getTrans('jobs.detail.commute.show')}
        </Link>
      )}
      {commute.isError && (
        <Text variant="caption" color="error.main" component="p" sx={{ mt: 1 }}>
          {getTrans('jobs.detail.commute.failed')}
        </Text>
      )}
    </Box>
  );
};

/** The geofence check at check-in and check-out, extra to the hours the talent reports themselves. */
export const LocationCheckNotice = ({ listing }: { listing: JobDetails }) =>
  listing.locationCheck === 'GEOFENCE' ? (
    <Notice
      testId="job-detail-location-check"
      severity="warning"
      title={getTrans('jobs.detail.locationCheck.title')}
      body={getTrans('jobs.detail.locationCheck.body')}
    />
  ) : null;

const FREQUENCY_ROWS: { key: keyof WeekendWork; label: TranslationKey }[] = [
  { key: 'saturday', label: 'jobs.detail.weekend.saturday' },
  { key: 'sunday', label: 'jobs.detail.weekend.sunday' },
  { key: 'holidays', label: 'jobs.detail.weekend.holidays' }
];

/** "Auch am Wochenende und an Feiertagen": the warning, the operator's note, and a row per day the job asks for. */
export const WeekendWorkBlock = ({ weekend }: { weekend?: WeekendWork }) => {
  if (!weekendDaysLabel(weekend)) return null;
  const rows = FREQUENCY_ROWS.filter(({ key }) => {
    const value = weekend?.[key] as WorkFrequency | undefined;
    return value === 'OCCASIONAL' || value === 'REGULAR';
  });

  return (
    <Box data-testid="job-detail-weekend" sx={{ mt: 1 }}>
      <Notice
        severity="warning"
        title={getTrans('jobs.detail.weekend.title')}
        body={weekend?.note}
      />
      {rows.map(({ key, label }) => (
        <Box
          key={key}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 0.5
          }}
        >
          <Text variant="body2" color="text.secondary">
            {getTrans(label)}
          </Text>
          <Text variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
            {getTrans(
              `jobs.detail.frequency.${
                weekend?.[key] as WorkFrequency
              }` as TranslationKey
            )}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

/** "So läuft deine Bewerbung": the opening's process as the tracker will show it, before anyone applied. */
export const ProcessSteps = ({ listing }: { listing: JobDetails }) => (
  <ProcessStepper
    steps={listing.processSteps ?? []}
    testId="job-detail-process"
  />
);

/** The banner over the working days: by arrangement, or fixed times to apply only for when they fit. */
export const WorkingTimeNotice = ({ listing }: { listing: JobDetails }) => {
  if (listing.workingTimeMode === 'FIXED') {
    return (
      <Notice
        testId="job-detail-working-time-mode"
        title={getTrans('jobs.detail.fixed.title')}
        body={getTrans('jobs.detail.fixed.body')}
      />
    );
  }
  if (listing.workingTimeMode === 'BY_ARRANGEMENT') {
    return (
      <Notice
        testId="job-detail-working-time-mode"
        title={getTrans('jobs.detail.byArrangement.title')}
        body={getTrans('jobs.detail.byArrangement.body')}
      />
    );
  }
  return null;
};

/** The open-ended contract explained, under its rows. */
export const OpenEndedNotice = () => (
  <Notice
    testId="job-detail-open-ended"
    title={getTrans('jobs.detail.openEnded.title')}
    body={getTrans('jobs.detail.openEnded.body')}
  />
);
